/**
 * Image types accepted by every editor entry point (toolbar picker,
 * drag-drop, paste). SVG is deliberately excluded — it can carry scripts
 * and the content pipeline treats uploads as inert images.
 */
export const ALLOWED_IMAGE_TYPES = [
	'image/png',
	'image/jpeg',
	'image/gif',
	'image/webp',
];

export const ALLOWED_IMAGE_ACCEPT = ALLOWED_IMAGE_TYPES.join(',');

/** Client-side upload cap; the server remains the authority. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export function isAllowedImage(file: File): boolean {
	return ALLOWED_IMAGE_TYPES.includes(file.type);
}

/**
 * Converts a data: URI (e.g. a base64 image pasted from Word or a web
 * page) into a File so it can be re-hosted through the upload service.
 * Returns null for malformed or non-allowed payloads.
 */
export function dataUrlToFile(
	dataUrl: string,
	fileName = 'pasted-image'
): File | null {
	const match = /^data:([^;,]+)?(;base64)?,([\s\S]*)$/.exec(dataUrl);
	if (!match) return null;
	const mime = match[1] || '';
	if (!ALLOWED_IMAGE_TYPES.includes(mime)) return null;
	const isBase64 = !!match[2];
	const data = match[3] || '';
	try {
		const bytes = isBase64 ? window.atob(data) : decodeURIComponent(data);
		const buffer = new Uint8Array(bytes.length);
		for (let i = 0; i < bytes.length; i += 1) {
			buffer[i] = bytes.charCodeAt(i);
		}
		const extension = mime.split('/')[1] || 'bin';
		return new File([buffer], `${fileName}.${extension}`, { type: mime });
	} catch {
		return null;
	}
}
